import { inject, Injectable } from '@angular/core';
import { hasActionsExecuting } from '@ngxs-labs/actions-executing';
import {
  Actions,
  Store,
  ofActionCompleted,
  ofActionSuccessful
} from '@ngxs/store';
import {
  ChangeUserPassword,
  GetRegistrationOneTimePassword,
  GetResetOneTimePassword,
  GoToNextRegistrationStep,
  HydrateLoggedUserProfile,
  LoginWithOneTimePassword,
  LoginWithPassword,
  LoginWithSocialNetwork,
  Logout,
  NavigateToLastRequestedRoute,
  PatchLoggedUser,
  PatchUserExistenceStatus,
  RefreshAccessToken,
  RegisterUser,
  ResetLoginState,
  ResetOnboardingForms,
  ResetUserPassword,
  SetLastRequestedRoute
} from '~backoffice/app/~store/actions/auth.actions';
import { AuthState } from '~backoffice/app/~store/states/auth.state';
import { Observable, filter, switchMap, take, withLatestFrom } from 'rxjs';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { IdentifyUserCompany } from '../actions/auth.actions';
import {
  GoToPreviousRegistrationStep,
  GoToRegistrationStep
} from '../actions/auth.actions';

@Injectable()
export class AuthFacade {
  protected _store = inject(Store);
  protected _actions = inject(Actions);

  currentUser$ = this._store.select(AuthState.currentUser);

  @SelectSnapshot(AuthState.currentUser) currentUser!: ReturnType<
    typeof AuthState.currentUser
  >;

  accessToken$ = this._store.select(AuthState.accessToken);

  @SelectSnapshot(AuthState.accessToken) accessToken!: ReturnType<
    typeof AuthState.accessToken
  >;

  refreshToken$ = this._store.select(AuthState.refreshToken);

  loginFormValues$ = this._store.select(AuthState.loginFormValues);

  locationFormValues$ = this._store.select(AuthState.locationFormValues);

  billingEquipmentFormValues$ = this._store.select(
    AuthState.billingEquipmentFormValues
  );

  loginCurrentUserData$ = this._store.select(AuthState.loginCurrentUserData);

  isUserKnown$ = this._store.select(AuthState.isUserKnown);

  @SelectSnapshot(AuthState.isUserKnown) isUserKnown!: ReturnType<
    typeof AuthState.isUserKnown
  >;

  challenge$ = this._store.select(AuthState.challenge);

  loginOtp$ = this._store.select(AuthState.loginOtp);

  loginOtpData$ = this._store.select(AuthState.loginOtpData);

  isLoginFormValid$ = this._store.select(AuthState.isLoginFormValid);

  registrationFormValues$ = this._store.select(
    AuthState.registrationFormValues
  );

  registeredUserData$ = this._store.select(AuthState.registeredUserData);

  registrationOtp$ = this._store.select(AuthState.registrationOtp);

  registrationStepper$ = this._store.select(AuthState.registrationStepper);

  isRegistrationFormValid$ = this._store.select(
    AuthState.isRegistrationFormValid
  );

  resetFormValues$ = this._store.select(AuthState.resetFormValues);

  resetRequestFormValues$!: Observable<
    ReturnType<typeof AuthState.resetRequestFormValues>
  >;

  resetUserData$ = this._store.select(AuthState.resetUserData);

  resetOtp$ = this._store.select(AuthState.resetOtp);

  resetOtpData$ = this._store.select(AuthState.resetOtpData);

  isResetFormValid$ = this._store.select(AuthState.isResetFormValid);

  isResetRequestFormValid$ = this._store.select(
    AuthState.isResetRequestFormValid
  );

  identifiedCompany$ = this._store.select(AuthState.identifiedCompany);

  // signals

  refreshToken = this._store.selectSignal(AuthState.refreshToken);

  readonly isTokenRefreshing$ = this._store.select(
    hasActionsExecuting([RefreshAccessToken])
  );

  readonly isLoggingIn$ = this._store.select(
    hasActionsExecuting([LoginWithPassword])
  );

  readonly isLoggingInWithPassword$ = this._store.select(
    hasActionsExecuting([LoginWithPassword])
  );

  readonly isLoggingInWithOtp$ = this._store.select(
    hasActionsExecuting([LoginWithOneTimePassword])
  );

  readonly isLoginFinishedWithOtp$ = this._actions.pipe(
    ofActionSuccessful(LoginWithPassword),
    withLatestFrom(this.loginOtp$),
    filter(([, otp]) => !!otp)
  );

  readonly isLoadingRegistrationOtp$ = this._store.select(
    hasActionsExecuting([GetRegistrationOneTimePassword])
  );

  readonly isRegisteringUser$ = this._store.select(
    hasActionsExecuting([RegisterUser])
  );

  readonly isGettingResetOtp$ = this._store.select(
    hasActionsExecuting([GetResetOneTimePassword])
  );

  readonly isResettingUserPassword$ = this._store.select(
    hasActionsExecuting([ResetUserPassword])
  );

  readonly isChangingPassword$ = this._store.select(
    hasActionsExecuting([ChangeUserPassword])
  );

  readonly isLoggingOut$ = this._store.select(hasActionsExecuting([Logout]));

  readonly isIdentifyingCompany$ = this._store.select(
    hasActionsExecuting([IdentifyUserCompany])
  );

  readonly isResetPasswordRequestFinishedWithOtp$ = this._actions.pipe(
    ofActionSuccessful(GetResetOneTimePassword),
    withLatestFrom(this.resetOtp$),
    filter(([, otp]) => !!otp)
  );

  readonly accessTokenHaveBeenRefreshed$ = this._actions.pipe(
    ofActionCompleted(RefreshAccessToken)
  );

  // Action Dispatchers
  loginWithPassword(
    ...params: ConstructorParameters<typeof LoginWithPassword>
  ): Observable<unknown> {
    return this._store.dispatch(new LoginWithPassword(...params));
  }

  loginWithOneTimePassword(
    ...params: ConstructorParameters<typeof LoginWithOneTimePassword>
  ): Observable<unknown> {
    return this._store.dispatch(new LoginWithOneTimePassword(...params));
  }

  loginWithSocialNetwork(
    ...params: ConstructorParameters<typeof LoginWithSocialNetwork>
  ): Observable<unknown> {
    return this._store.dispatch(new LoginWithSocialNetwork(...params));
  }

  getRegistrationOneTimePassword(
    ...params: ConstructorParameters<typeof GetRegistrationOneTimePassword>
  ): Observable<unknown> {
    return this._store.dispatch(new GetRegistrationOneTimePassword(...params));
  }

  goToNextRegistrationStep(
    ...params: ConstructorParameters<typeof GoToNextRegistrationStep>
  ): Observable<unknown> {
    return this._store.dispatch(new GoToNextRegistrationStep(...params));
  }

  goToPreviousRegistrationStep(
    ...params: ConstructorParameters<typeof GoToPreviousRegistrationStep>
  ): Observable<unknown> {
    return this._store.dispatch(new GoToPreviousRegistrationStep(...params));
  }

  goToRegistrationStep(
    ...params: ConstructorParameters<typeof GoToRegistrationStep>
  ): Observable<unknown> {
    return this._store.dispatch(new GoToRegistrationStep(...params));
  }

  registerUser(
    ...params: ConstructorParameters<typeof RegisterUser>
  ): Observable<unknown> {
    return this._store.dispatch(new RegisterUser(...params));
  }

  getResetOneTimePassword(
    ...params: ConstructorParameters<typeof GetResetOneTimePassword>
  ): Observable<unknown> {
    return this._store.dispatch(new GetResetOneTimePassword(...params));
  }

  resetUserPassword(
    ...params: ConstructorParameters<typeof ResetUserPassword>
  ): Observable<unknown> {
    return this._store.dispatch(new ResetUserPassword(...params));
  }

  changeUserPassword(
    ...params: ConstructorParameters<typeof ChangeUserPassword>
  ): Observable<void> {
    return this._store.dispatch(new ChangeUserPassword(...params));
  }

  refreshAccessToken(
    ...params: ConstructorParameters<typeof RefreshAccessToken>
  ): Observable<unknown> {
    return this.isTokenRefreshing$.pipe(
      take(1),
      switchMap(isTokenRefreshing =>
        isTokenRefreshing
          ? this.accessTokenHaveBeenRefreshed$.pipe(filter(Boolean), take(1))
          : this._store.dispatch(new RefreshAccessToken(...params))
      )
    );
  }

  resetLoginState(
    ...params: ConstructorParameters<typeof ResetLoginState>
  ): Observable<unknown> {
    return this._store.dispatch(new ResetLoginState(...params));
  }

  setLastRequestedRoute(
    ...params: ConstructorParameters<typeof SetLastRequestedRoute>
  ): Observable<unknown> {
    return this._store.dispatch(new SetLastRequestedRoute(...params));
  }

  navigateToLastRequestedRoute(
    ...params: ConstructorParameters<typeof NavigateToLastRequestedRoute>
  ): Observable<unknown> {
    return this._store.dispatch(new NavigateToLastRequestedRoute(...params));
  }

  logout(...params: ConstructorParameters<typeof Logout>): Observable<unknown> {
    return this._store.dispatch(new Logout(...params));
  }

  patchLoggedUser(
    ...params: ConstructorParameters<typeof PatchLoggedUser>
  ): Observable<unknown> {
    return this._store.dispatch(new PatchLoggedUser(...params));
  }

  patchUserExistenceStatus(
    ...params: ConstructorParameters<typeof PatchUserExistenceStatus>
  ): Observable<unknown> {
    return this._store.dispatch(new PatchUserExistenceStatus(...params));
  }

  identifyUserCompany(
    ...params: ConstructorParameters<typeof IdentifyUserCompany>
  ): Observable<unknown> {
    return this._store.dispatch(new IdentifyUserCompany(...params));
  }

  resetOnboardingForms(
    ...params: ConstructorParameters<typeof ResetOnboardingForms>
  ): Observable<unknown> {
    return this._store.dispatch(new ResetOnboardingForms(...params));
  }

  hydrateLoggedUserProfile(
    ...params: ConstructorParameters<typeof HydrateLoggedUserProfile>
  ): Observable<unknown> {
    return this._store.dispatch(new HydrateLoggedUserProfile(...params));
  }
}
